import React, { useEffect, useState } from "react";
import { Display, TableProps, ActivityCard, Table } from "@apptronik/react-exoskeleton";
// 💡 Added Box and Chip for layout and styling
import { Box, Button, Chip } from "@mui/material";
import { EditPersonDialog } from "./EditPersonDialog";
import { difference } from "../role/RolePage";
import query_data from "../../../js/query_data";
import { isAdmin } from "../../../js/auth_helpers";

type Columns = {
    "username": string;
    "roles": React.ReactNode; // Changed to ReactNode to allow components
    "edit"?: React.ReactNode; // Changed to ReactNode for consistency
}

export default function PeoplePage() {
    const [people, setPeople] = useState([]);
    const [self, setSelf] = useState(null);

    const [openEditUser, setOpenEditUser] = useState<boolean>(false);
    const [selectedUserName, setSelectedUserName] = useState<string>('');
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [allRoles, setAllRoles] = useState([]);

    const cards = [{ label: "Active", color: "green", value: people.length }]; // Made value dynamic

    const userIsAdmin = (): boolean => {
        return isAdmin(self);
    }

    const handleOpenEdit = (name, roles) => {
        const roleNames = roles.map(role => role.name);
        setSelectedUserName(name);
        setSelectedRoles(roleNames);
        setOpenEditUser(true);
    };

    const handleCancel = () => {
        setSelectedUserName('');
        setSelectedRoles([]);
        setOpenEditUser(false);
    };

    const handleSubmit = async (userName, requestedRoles) => {
        const user = people.find(p => p.username === userName);
        if (!user) return;

        const newRoles = difference(requestedRoles, selectedRoles);
        const removedRoles = difference(selectedRoles, requestedRoles);

        const addPromises = newRoles.map(roleName => {
            const role = allRoles.find(r => r.name === roleName);
            if (role) return query_data(`/users/role/${user.id}`, role.id, 'POST');
            return Promise.resolve();
        });

        const removePromises = removedRoles.map(roleName => {
            const role = allRoles.find(r => r.name === roleName);
            if (role) return query_data(`/users/role/${user.id}`, role.id, 'DELETE');
            return Promise.resolve();
        });

        await Promise.all([...addPromises, ...removePromises]);
        setOpenEditUser(false);
    }
    
    useEffect(() => {
        const fetchAllData = async () => {
            // Fetch all data in parallel for faster loading
            const [rolesData, selfData, peopleData] = await Promise.all([
                query_data('/roles', {}),
                query_data('/users/get_self', {}),
                query_data('/users', {})
            ]);
            setAllRoles(rolesData);
            setSelf(selfData[0]);
            setPeople(peopleData);
        };
        fetchAllData();
    }, [openEditUser]);


    const rows = people.map((peep) => {
        // ✨ Instead of joining to a string, we render Chips in a flex container
        const roles = (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {peep.roles.map((role) => (
                    <Chip label={role.name} key={role.id} size="small" variant="outlined" />
                ))}
            </Box>
        );

        const rowsConfig = {
            link: ``,
            cells: {
                username: peep.username ?? 'N/A',
                roles: roles // The cell now contains JSX
            }
        }
        if (userIsAdmin()) {
            rowsConfig.cells["edit"] = (
                <Box sx={{ minWidth: '80px' }}>
                     <Button size="small" onClick={() => handleOpenEdit(peep.username, peep.roles)}>
                        Edit
                    </Button>
                </Box>
            );
        }
        return rowsConfig;
    });

    const TableInput: TableProps<Columns> = {
        rows,
    }

    return (
        // 💡 Simplified the main layout container
        <Box sx={{ padding: '2.5rem' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                    <Display variant='medium'>
                        People
                    </Display>
                    <Box sx={{ mt: 2 }}>
                        {cards?.map((card, index) => <ActivityCard key={index} label={card.label} color={card.color} value={card.value} />)}
                    </Box>
                </Box>
                {/* You could add a "Create Person" button here if needed */}
            </Box>

            <EditPersonDialog
                open={openEditUser}
                handleCancel={handleCancel}
                handleSubmit={handleSubmit}
                userName={selectedUserName}
                roleOptions={allRoles.map(role => role.name)}
                existingRoles={selectedRoles}
            />

            {rows.length > 0 ? <Table {...TableInput} /> : <p>No people found.</p>}
        </Box>
    );
}